import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { Column, Row } from "components/Layout";
import Checkbox from "components/Checkbox";
import styles from "./_charts.module.scss";


const turboThreshold = 10000;

function getFeatureType(feature) {
	if (!feature?.name.includes('#')) return undefined
	return feature.name.split('#')[0]
}

const baseParameters = [
	'atmHumidity',
	'atmTemperature',
	'soilTemperature',
	'soilWaterTension',
	'leafHumidity',
	'rainfall',
	'radiation',
	'windSpeed',
	'atmPressure',
]

const filters = {
	'main': a => [...baseParameters, 'absoluteDistance', 'relativeDistance'].includes(getFeatureType(a)),
	'forecast': a => ['atmTemperature', 'atmHumidity', 'atmPressure', 'windSpeed', 'rainfall'].includes(getFeatureType(a)),
	'freezing': a => ['dewPoint', 'wetBulbTemperature', 'atmTemperature', 'atmHumidity', 'deltaTemperature'].includes(getFeatureType(a)),
	'irrigation': a => ['rainfall', 'soilWaterTension', 'atmTemperature', 'atmHumidity', 'radiation', 'windSpeed', 'vaporPressureDeficit', 'deltaTemperature'].includes(getFeatureType(a)),
	'phytosanitary': a => ['rainfall', 'atmTemperature', 'atmHumidity', 'leafHumidity', 'windSpeed'].includes(getFeatureType(a)),
	// statistics and adversities charts
	'stats': a => [...baseParameters, 'evapotraspirazioneArboree', 'evapotraspirazioneErbacee'].includes(getFeatureType(a)),
	'olivo-cicloconio': a => ['atmTemperature', 'atmHumidity', 'leafHumidity', 'risk'].includes(getFeatureType(a)),
	'melo-ticchiolatura': a => ['atmTemperature', 'atmHumidity', 'leafHumidity', 'ticchiolaturaDegreeHour'].includes(getFeatureType(a)),
}

const statTypes = ['stats', 'olivo-cicloconio', 'melo-ticchiolatura']


function createAxis({ id, text, format, color, ...otherProps }) {
	let plotLines = null
	if (id === 'axis-temperature') {
		plotLines = [{
			value: 0,
			color,
			width: 1,
			zIndex: 4,
			label: { text: "0°C", style: { color } }
		}]
	}

	return {
		id,
		gridLineWidth: 0,
		color,
		title: {
			style: { color },
			text
		},
		labels: {
			style: { color },
			format
		},
		plotLines,
		showLastLabel: false,
		visible: true,
		...otherProps
	};
}

function axisIndexById(id) {
	return chartAxes.findIndex(a => a.id === id)
}

let chartAxes = [
	{
		id: "axis-default",
		text: "",
		format: "{value}",
		opposite: false,
		color: 'black',
	},
	{
		id: 'axis-humidity',
		text: "Umidità'",
		format: "{value}%",
		opposite: true,
		min: 0,
		max: 100,
		color: '#00BCD4',
	},
	{
		id: 'axis-temperature',
		text: "Temperatura",
		format: "{value}°C",
		opposite: true,
		color: '#B71C1C',
	},
	{
		id: 'axis-waterPotential',
		text: "Potenziale Idrico",
		format: "{value} cb",
		opposite: true,
		color: '#808900',
		reversed: true,
		min: 0,
	},
	{
		id: "axis-leafHumidity",
		label: "BAGNATURA FOGLIARE",
		text: "Bagnatura Fogliare",
		format: "{value}%",
		opposite: false,
		min: 0,
		max: 100,
		color: '#8BC34A',
	},
	{
		id: "axis-rainfall",
		text: "Pioggia",
		format: "{value} mm",
		opposite: false,
		color: '#0d47a1',
	},
	{
		id: "axis-radiation",
		text: "Radiazione Solare",
		format: "{value} W/mq",
		opposite: false,
		color: '#ffc107',
	},
	{
		id: 'axis-windSpeed',
		text: "vento",
		format: "{value} km/h",
		opposite: false,
		color: '#9b9b9b',
	},
	{
		id: "axis-atmPressure",
		text: "pressione",
		format: "{value} hPa",
		opposite: false,
		color: '#000000',
	},
	{
        id: "axis-vpd",
        text: "VPD",
        format: "{value} kPa",
        opposite: false,
        color: '#000000',
    },
	{
		id: "axis-et0",
		text: "Evapotraspirazione potenziale (ET0)",
		format: "{value} mm/day",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-distance",
		text: "distanza",
		format: "{value} cm",
		opposite: false,
		color: '#9013FE',
	},
	{
		id: "axis-risk",
		text: "Rischio Avversità",
		format: "{value}",
		opposite: false,
		min: 0,
		max: 10,
		color: 'black',
	},
	{
		id: "axis-degreeHour",
		text: "Gradi Ora",
		format: "{value} °ora",
		opposite: false,
		min: 0,
		color: 'black',
	},
].map(axis => createAxis(axis))

let chartSeries = [
	{
		id: "atmHumidity",
		label: "UMIDITA' ATMOSFERICA",
		color: '#0097A7',
		series: {
			name: 'Umidità',
			type: 'spline',
			tooltip: { valueSuffix: '%' },
			yAxis: axisIndexById('axis-humidity'),
		},
	},
	{
		id: "atmTemperature",
		label: "TEMPERATURA ATMOSFERICA",
		color: '#B71C1C',
		series: {
			name: 'Temp. atm.',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "soilTemperature",
		label: "TEMPERATURA SUOLO",
		color: '#ff6f00',
		series: {
			name: 'Temp. Suolo',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "soilWaterTension",
		label: "POTENZIALE IDRICO",
		color: '#757d00',
		series: {
			name: 'Potenziale Idrico',
			type: 'spline',
			tooltip: { valueSuffix: ' cb' },
			yAxis: axisIndexById('axis-waterPotential'),
		},
	},
	{
		id: "leafHumidity",
		label: "BAGNATURA FOGLIARE",
		color: '#8BC34A',
		series: {
			name: 'Umidità Fogl.',
			type: 'spline',
			tooltip: { valueSuffix: ' %' },
			yAxis: axisIndexById('axis-leafHumidity'),
		},
	},
	{
		id: "rainfall",
		label: "PIOGGIA",
		color: '#0d47a1',
		series: {
			name: 'Pioggia',
			type: 'spline',
			tooltip: { valueSuffix: ' mm' },
			yAxis: axisIndexById('axis-rainfall'),
		},
	},
	{
		id: "radiation",
		label: "RADIAZIONE SOLARE",
		color: '#ffc107',
		series: {
			name: 'UV',
			type: 'spline',
			tooltip: { valueSuffix: ' W/mq' },
			yAxis: axisIndexById('axis-radiation'),
		},
	},
	{
		id: "windSpeed",
		label: "VELOCITA' DEL VENTO",
		color: '#9b9b9b',
		series: {
			name: 'vento',
			type: 'spline',
			tooltip: { valueSuffix: ' km/h' },
			yAxis: axisIndexById('axis-windSpeed'),
		},
	},
	{
		id: "atmPressure",
		label: "PRESSIONE ATMOSFERICA",
		color: '#000000',
		series: {
			name: 'pressione',
			type: 'spline',
			tooltip: { valueSuffix: ' hPa' },
			yAxis: axisIndexById('axis-atmPressure'),
		},
	},
	{
		id: "absoluteDistance",
		label: "DISTANZA ASSOLUTA",
		color: '#9013FE',
		series: {
			name: 'distanza assoluta',
			type: 'spline',
			tooltip: { valueSuffix: ' cm' },
			yAxis: axisIndexById('axis-distance'),
		},
	},
	{
		id: "relativeDistance",
		label: "DISTANZA RELATIVA",
		color: '#BD10E0',
		series: {
			name: 'distanza relativa',
			type: 'spline',
			tooltip: { valueSuffix: ' cm' },
			yAxis: axisIndexById('axis-distance'),
		},
	},
	{
		id: "dewPoint",
		label: "PUNTO DI RUGIADA",
		color: '#653294',
		series: {
			name: 'Punto Rugiada',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "wetBulbTemperature",
		label: "TEMPERATURA BULBO UMIDO",
		color: '#ab149e',
		series: {
			name: 'Temp. Bulbo Umido',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "deltaTemperature",
		label: "DELTA DI TEMPERATURA",
		color: '#ab8314ff',
		series: {
			name: 'Delta T',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "vaporPressureDeficit",
		label: "DEFICIT DI PRESSIONE DI VAPORE (VPD)",
		color: '#000000',
		series: {
			name: 'VPD',
			type: 'spline',
			tooltip: { valueSuffix: ' kPa' },
			yAxis: axisIndexById('axis-vpd'),
		},
	},
	{
		id: "evapotraspirazioneArboree",
		label: "EVAPOTRASPIRAZIONE POTENZIALE ARBOREE (ET0)",
		color: '#000000',
		series: {
			name: 'ET0 arboree',
			type: 'spline',
			tooltip: { valueSuffix: ' mm/day' },
			yAxis: axisIndexById('axis-et0'),
		},
	},
	{
		id: "evapotraspirazioneErbacee",
		label: "EVAPOTRASPIRAZIONE POTENZIALE ERBACEE (ET0)",
		color: '#666666',
		series: {
			name: 'ET0 erbacee',
			type: 'spline',
			tooltip: { valueSuffix: ' mm/day' },
			yAxis: axisIndexById('axis-et0'),
		},
	},
	{
		id: "risk",
		label: "RISCHIO AVVERSITA'",
		color: '#000000',
		series: {
			name: 'Rischio Avversità',
			type: 'column',
			tooltip: { valueSuffix: '/10' },
			yAxis: axisIndexById('axis-risk'),
			zoneAxis: 'y',
			zones: [
				{ value: 0, color: 'green' },
				{ value: 3, color: 'green' },
				{ value: 6, color: 'orange' },
				{ color: 'red' },
			],
		},
	},
	{
		id: "ticchiolaturaDegreeHour",
		label: "RISCHIO AVVERSITA'",
		color: '#000000',
		series: {
			name: 'Rischio Avversità',
			type: 'column',
			tooltip: { valueSuffix: ' °ora' },
			yAxis: axisIndexById('axis-degreeHour'),
			zoneAxis: 'y',
			zones: [
				{ value: 0, color: 'green' },
				{ value: 130, color: 'green' },
				{ value: 150, color: 'yellow' },
				{ value: 180, color: 'orange' },
				{ color: 'red' },
			],
		},
	},
].map(series => {
	series.series.color = series.color
	series.series.turboThreshold = turboThreshold
	return series
});

let _chartOptions = {
	legend: { enabled: false },
	title: { text: null },
	chart: { zoomType: 'xy' },
	xAxis: { type: 'datetime', gridLineDashStyle: 'dash', gridLineWidth: 1 },
	yAxis: chartAxes,
	tooltip: { shared: true },
	time: { timezone: 'Europe/Rome', useUTC: false },
};


export default function ChartsV2({ data, device, type }) {
	const [selectedFeatures, setSelectedFeatures] = useState([])
	const [allFeatures, setAllFeatures] = useState(false)
	const [chartOptions, setChartOptions] = useState(_chartOptions)

	const features = useMemo(() => {
		let features = device?.configuration?.parameters ?? []
		return features.filter(filters[type] || filters['main']).sort((a, b) => a.name.localeCompare(b.name))
	}, [device, type])

	useEffect(() => {
		let fnames = features.map(a => a.name)
		setSelectedFeatures(fnames.length ? [fnames[0]] : [])
	}, [features])

	useEffect(updateChart, [data, features, selectedFeatures, type])

	function updateChart() {
		let chartOptions = Object.assign({}, _chartOptions)
		let series = [];

		chartAxes.forEach(a => a.visible = false)
		// evito che nei grafici statistici la data sia disallineata per via del fuso orario
		chartOptions.time.useUTC = statTypes.includes(type)
		features.forEach(a => {
			let serie = Object.assign({}, chartSeries.find(s => s.id === getFeatureType(a))?.series ?? {
				color: '#000',
				type: 'spline',
				tooltip: { valueSuffix: ` ${a.unit}` },
				yAxis: axisIndexById('axis-default'),
			})
			serie.name = a.labelShort
			serie.data = data.map(d => ({
				name: moment(d.collectionDate).format(statTypes.includes(type) ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"),
				x: d.collectionDate,
				y: d.paramMap[a.name],
			})).filter(d => !!d.y || d.y === 0)
			const isVisible = selectedFeatures.findIndex(sf => sf === a.name) > -1
			serie.visible = isVisible
			if (!serie.zoneAxis) {
				serie.zoneAxis = 'x'
			}
			if (serie.zoneAxis === 'x') {
				serie.zones = [{ value: moment().valueOf() }, { dashStyle: 'dash' }]
			}
			series.push(serie)
			if (isVisible) {
				chartAxes[serie.yAxis].visible = true
			}
		})
		chartOptions.series = series
		setChartOptions(chartOptions)
	}

	function addLabel(label) {
		let sl = selectedFeatures.slice();
		sl.push(label);
		setSelectedFeatures(sl);
	}

	function removeLabel(label) {
		let sl = selectedFeatures.slice();
		sl = sl.filter(l => l !== label);
		setSelectedFeatures(sl);
	}

	function checkAllFeatures() {
		let allFeaturesNew = !allFeatures
		let newSelectedFeatures = allFeaturesNew? features.map(a => a.name): []
		setAllFeatures(allFeaturesNew)
		setSelectedFeatures(newSelectedFeatures)
	}

	return <>
		<Row>
			<Column className={classNames(styles.box, styles.chartContainer)}>
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					immutable={true}
				/>
			</Column>
		</Row>
		<Row>
			<Column className={classNames(styles.box, styles.legendContainer)}>
				<div className={styles.legendContainer__header}>
					<div className={styles["legendContainer__header-title"]}>LEGENDA</div>
					<div className={styles["legendContainer__header-subtitle"]}>Scegli quale dato visualizzare</div>
				</div>

				<div className={styles.legendOptions}>
					{features.map(f =>
						<div className={styles.legendOptions__option} key={`${f.name}-option`}>
							<Checkbox
								color={"red"}
								onCheck={() => selectedFeatures.findIndex(sf => sf === f.name) < 0 ? addLabel(f.name) : removeLabel(f.name)}
								checked={selectedFeatures.findIndex(sf => sf === f.name) !== -1}
							/>
							<div className={styles.legendOptions__label}>
								<div>
									<div className={styles.legendOptions__optionFlag}
										style={{ backgroundColor: chartSeries.find(s => s.id === getFeatureType(f))?.color ?? '#000', marginRight: 15 }} />
								</div>
								{f.labelLong.toLocaleUpperCase()}
							</div>
						</div>
					)}
				</div>

				<div className={styles.legendOptions} style={{marginTop: 0}}>
					<div className={styles.legendOptions__option}>
						<Checkbox
							color={"red"}
							onCheck={checkAllFeatures}
							checked={allFeatures}
						/>
						<div className={styles.legendOptions__label}>
							SELEZIONA TUTTO
						</div>
					</div>
				</div>
			</Column>
		</Row>
	</>
}
